import { httpClient } from '@/lib/httpClient';

export interface Prompt {
  id?: string;
  promptName: string;
  serviceName: string;
  customerId?: number | null;
  version: string;
  main?: boolean;
  template: string;
  responseSchema?: Record<string, any>;
  variables?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PromptVariable {
  id?: string;
  name: string;
  query: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CreatePromptDto {
  promptName: string;
  serviceName: string;
  template: string;
  customerId?: number;
  responseSchema?: Record<string, any>;
  main?: boolean;
}

export interface CreatePromptVariableDto {
  name: string;
  query: string;
}

export interface PromptsResponse {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: Prompt[];
}

export interface PromptVariableResponse {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: PromptVariable[];
}

export interface FilterPromptsDto {
  serviceName?: string;
  promptName?: string;
  customerId?: number;
  page?: number;
  limit?: number;
}

export interface FilterPromptVariablesDto {
  name?: string[];
  page?: number;
  limit?: number;
}

export interface RenderPromptDto {
  promptName: string;
  serviceName: string;
  customerId?: number;
  version?: string;
  queryParams?: Record<string, string | number | boolean>;
}

export interface RenderPromptResponse {
  promptName: string;
  template: string;
  variables: string[];
  renderedTemplate: string;
  responseSchema?: Record<string, any>;
  queryParams?: Record<string, string | number | boolean>;
}

export interface TestQueryDto {
  query: string;
  queryParams?: Record<string, string | number | boolean>;
}

export interface TestQueryResponse {
  valid: boolean;
  message: string;
  sampleResult?: string;
  resultCount?: number;
}

export interface uniqueValues {
  serviceNames: string[];
  promptNames: string[];
}

class PromptsService {
  constructor() {}

  async getPrompts(filter: FilterPromptsDto): Promise<PromptsResponse> {
    const result = await httpClient.get(`/v1/prompts`, {
      params: {
        serviceName: filter.serviceName,
        promptName: filter.promptName,
        customerId: filter.customerId,
        page: filter.page,
        limit: filter.limit,
      },
    });
    return result.data;
  }

  async createPrompt(createPromptDto: CreatePromptDto): Promise<Prompt> {
    const result = await httpClient.post(`/v1/prompts`, createPromptDto);
    return result.data;
  }

  async getPromptById(id: string): Promise<Prompt | null> {
    const result = await httpClient.get(`/v1/prompts/${id}`);
    return result.data;
  }

  async setAsMain(id: string): Promise<Prompt> {
    const result = await httpClient.patch(`/v1/prompts/${id}/main`);
    return result.data;
  }

  async updatePrompt(
    id: string,
    prompt: Partial<Prompt>,
  ): Promise<Prompt | null> {
    const result = await httpClient.patch(`/v1/prompts/${id}`, prompt);
    return result.data;
  }

  async removePrompt(id: string) {
    await httpClient.delete(`/v1/prompts/${id}`);
    return true;
  }

  async getPromptVariables(
    filter: FilterPromptVariablesDto,
  ): Promise<PromptVariableResponse> {
    const result = await httpClient.get(`/v1/prompts/variables`, {
      params: {
        name: filter.name,
        page: filter.page,
        limit: filter.limit,
      },
    });
    return result.data;
  }

  async createPromptVariable(
    variable: PromptVariable,
  ): Promise<PromptVariable> {
    const result = await httpClient.post(`/v1/prompts/variables`, variable);
    return result.data;
  }

  async getPromptVariableById(id: string): Promise<PromptVariable | null> {
    const result = await httpClient.get(`/v1/prompts/variables/${id}`);
    return result.data;
  }

  async updatePromptVariable(
    id: string,
    variable: Partial<PromptVariable>,
  ): Promise<PromptVariable | null> {
    const result = await httpClient.patch(
      `/v1/prompts/variables/${id}`,
      variable,
    );
    return result.data;
  }

  async removePromptVariable(id: string): Promise<boolean> {
    const result = await httpClient.delete(`/v1/prompts/variables/${id}`);
    return result.status === 204;
  }

  async renderPrompt(
    renderPromptDto: RenderPromptDto,
  ): Promise<RenderPromptResponse> {
    const result = await httpClient.post(
      `/v1/prompts/render-root`,
      renderPromptDto,
    );
    return result.data;
  }

  async testQuery(testQueryDto: TestQueryDto): Promise<TestQueryResponse> {
    const result = await httpClient.post(
      `/v1/prompts/variables/test-query`,
      testQueryDto,
    );
    return result.data;
  }
  async getUniqueValues(): Promise<uniqueValues> {
    const result = await httpClient.get(`/v1/prompts/filter-options`);
    return result.data;
  }
}

export default new PromptsService();
